import type { DtcSeverity } from '@fleetcore/shared/telemetry'

import type { DtcEventView } from './api-types'

/**
 * How a list of fault codes is ordered, summarised and grouped for reading.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 *  AN UNKNOWN CODE IS STILL A FAULT.
 * ─────────────────────────────────────────────────────────────────────────────
 * Nothing here filters on `descriptionKnown`. A code the dictionary does not carry
 * sorts, counts and renders exactly like one it does — with its raw mnemonic and an
 * explicit "description inconnue" — because the alternative is that a manufacturer's
 * engine fault disappears from a fleet manager's screen for the sole reason that we
 * never translated it. The shared dictionary grades an unnamed code as `warning`, not
 * `info`, for the same reason, and this module does not second-guess it.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 *  WHY SEVERITY, THEN RECENCY.
 * ─────────────────────────────────────────────────────────────────────────────
 * A list ordered by time buries a critical injector fault raised this morning under
 * six evaporative-emission codes raised at lunchtime. Severity first is what makes the
 * top of the list the thing to act on; recency inside a severity is what makes two
 * criticals readable.
 */

/** Worst first. The order the UI reads in, and the order `worstSeverity` ranks by. */
export const DTC_SEVERITY_ORDER: readonly DtcSeverity[] = ['critical', 'warning', 'info']

export function severityRank(severity: DtcSeverity): number {
  const index = DTC_SEVERITY_ORDER.indexOf(severity)
  // An unrecognised severity sorts to the TOP, not the bottom: a value this app does
  // not know about is a contract change, and hiding it at the end of a list is how a
  // new severity ships unnoticed.
  return index === -1 ? -1 : index
}

export function worstSeverity(events: readonly DtcEventView[]): DtcSeverity | null {
  return events.reduce<DtcSeverity | null>(
    (worst, event) =>
      worst === null || severityRank(event.severity) < severityRank(worst) ? event.severity : worst,
    null,
  )
}

/** Severity first, then newest occurrence, then the code itself so ties are stable. */
export function sortDtcEvents(events: readonly DtcEventView[]): DtcEventView[] {
  return [...events].sort((a, b) => {
    const bySeverity = severityRank(a.severity) - severityRank(b.severity)
    if (bySeverity !== 0) return bySeverity
    const byTime = Date.parse(b.lastSeenAt) - Date.parse(a.lastSeenAt)
    if (!Number.isNaN(byTime) && byTime !== 0) return byTime
    return a.code.localeCompare(b.code)
  })
}

export interface DtcSummary {
  total: number
  critical: number
  warning: number
  info: number
  /** Codes carrying no description. Surfaced as a number, never as a silent omission. */
  unknownDescriptions: number
  /** Tokens that are not even SAE-shaped. The device itself is the fault being reported. */
  malformed: number
  worst: DtcSeverity | null
}

export function summariseDtc(events: readonly DtcEventView[]): DtcSummary {
  return {
    total: events.length,
    critical: events.filter((event) => event.severity === 'critical').length,
    warning: events.filter((event) => event.severity === 'warning').length,
    info: events.filter((event) => event.severity === 'info').length,
    unknownDescriptions: events.filter((event) => !event.descriptionKnown).length,
    malformed: events.filter((event) => event.malformed).length,
    worst: worstSeverity(events),
  }
}

/**
 * How many times a code has occurred, for the history view.
 *
 * A misfire that appears every cold morning and clears by ten is the single most
 * valuable pattern a maintenance history holds, and it is only visible when the
 * occurrences are counted per code rather than listed as unrelated rows.
 */
export interface DtcRecurrence {
  code: string
  occurrences: number
  firstSeenAt: string
  lastSeenAt: string
  severity: DtcSeverity
  description: string | null
  descriptionKnown: boolean
  stillActive: boolean
}

export function groupRecurrences(events: readonly DtcEventView[]): DtcRecurrence[] {
  const byCode = new Map<string, DtcRecurrence>()

  for (const event of events) {
    const existing = byCode.get(event.code)
    if (existing === undefined) {
      byCode.set(event.code, {
        code: event.code,
        occurrences: 1,
        firstSeenAt: event.firstSeenAt,
        lastSeenAt: event.lastSeenAt,
        severity: event.severity,
        description: event.description,
        descriptionKnown: event.descriptionKnown,
        stillActive: event.active,
      })
      continue
    }

    byCode.set(event.code, {
      ...existing,
      occurrences: existing.occurrences + 1,
      firstSeenAt:
        Date.parse(event.firstSeenAt) < Date.parse(existing.firstSeenAt)
          ? event.firstSeenAt
          : existing.firstSeenAt,
      lastSeenAt:
        Date.parse(event.lastSeenAt) > Date.parse(existing.lastSeenAt)
          ? event.lastSeenAt
          : existing.lastSeenAt,
      stillActive: existing.stillActive || event.active,
    })
  }

  return [...byCode.values()].sort((a, b) => {
    const bySeverity = severityRank(a.severity) - severityRank(b.severity)
    if (bySeverity !== 0) return bySeverity
    if (a.occurrences !== b.occurrences) return b.occurrences - a.occurrences
    return a.code.localeCompare(b.code)
  })
}
